// src/components/modals/DeleteConfirmationModal.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Alert } from 'react-bootstrap';
import useModalHistory from '../../../hooks/useModalHistory';

const DeleteConfirmationModal = ({ show, onHide, onConfirm, item }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

    useModalHistory(show, onHide, 'files-delete-modal');
  
  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      setIsDeleting(false);
      setError(null);
    }
  }, [show]);
  
  // Handle confirm button click
  const handleConfirm = async () => {
    if (!item) return;
    
    setIsDeleting(true);
    setError(null);
    
    try {
      await onConfirm();
      // The parent component will handle closing the modal on success
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
      setIsDeleting(false);
    }
  };
  
  // Get item name and type for display
  const getItemDetails = () => {
    if (!item) return { name: 'item', type: 'item', count: 0 };
    
    if (item.type === 'multiple') {
      return {
        name: 'selected items',
        type: 'items',
        count: item.items.length
      };
    }
    
    return {
      name: item.name,
      type: item.type,
      count: 1
    };
  };
  
  const { name, type, count } = getItemDetails();
  
  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className='common-modal'
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Confirm Deletion
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        
        <div className="text-center mb-3">
          <i className="fas fa-exclamation-triangle text-danger" style={{ fontSize: '3rem' }}></i>
        </div>
        
        {count > 1 ? (
          <p>
            Are you sure you want to delete <strong>{count} items</strong>?
            <br />
            This action cannot be undone.
          </p>
        ) : (
          <p>
            Are you sure you want to delete {type === 'folder' ? 'the folder' : 'the file'} <strong>{name}</strong>?
            {type === 'folder' && ' All contents will be deleted as well.'}
            <br />
            This action cannot be undone.
          </p>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={onHide}
          disabled={isDeleting}
        >
          Cancel
        </Button>
        <Button 
          variant="danger" 
          onClick={handleConfirm}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <>
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
              Deleting...
            </>
          ) : 'Delete'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

DeleteConfirmationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  item: PropTypes.oneOfType([
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired
    }),
    PropTypes.shape({
      type: PropTypes.oneOf(['multiple']).isRequired,
      items: PropTypes.array.isRequired
    })
  ])
};

export default DeleteConfirmationModal;